import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static classes = ['hidden'];

  static targets = ['inputField', 'openLink', 'remove', 'previewContainer', 'previewImage'];

  static values = {
    country: String,
    environment: String,
    ownerId: String,
    url: String,
  }

  apiKey () {
    return this.inputFieldTarget.dataset.apiKey;
  }

  autoSubmit () {
    return this.inputFieldTarget.dataset.autoSubmit;
  }

  cloudName () {
    return this.inputFieldTarget.dataset.cloudName;
  }

  connect () {
    this.setupCloudinaryScript();

    const value = this.inputFieldTarget.dataset.value || this.urlValue || ''
    this.inputFieldTarget.value = value

    this.showPreviewOnConnect(value)
  }

  customTranslations () {
    return this.inputFieldTarget.dataset.translations;
  }

  defaultUploadPreset () {
    return 'p5btpvca';
  }

  getContextMetadata () {
    return {
      country: this.countryValue,
      environment: this.environmentValue,
      owner_id: this.ownerIdValue,
    }
  }

  hidePreview () {
    if (!this.hasPreviewContainerTarget) return;

    this.previewContainerTarget.classList.add(this.hiddenClass);
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.classList.add(this.hiddenClass);
    }
    if (this.hasRemoveTarget) {
      this.removeTarget.classList.add(this.hiddenClass);
    }
  }

  imageUrl (uploadInfoPath) {
    return `https://res.cloudinary.com/${this.cloudName()}/image/upload/${uploadInfoPath}`;
  }

  loadUploaderScript () {
    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';

    this.openLinkTarget.appendChild(script);
    window.cloudinaryIsLoaded = true;

    // allow upload script some time to load and initialize
    setTimeout(this.setupCloudinaryScript.bind(this), 100);
  }

  multiple () {
    return this.inputFieldTarget.dataset.multiple || false;
  }

  setupCloudinaryScript() {
    if (!window.cloudinaryIsLoaded) {
      setTimeout(this.loadUploaderScript.bind(this), 100);
    } else {
      this.setupCloudinaryUploadWidget();
    }
  }

  translationsExists (translations) {
    return translations && !translations.includes("Translation missing")
  }

  removeImage () {
    const inputField = this.inputFieldTarget;
    const previewImage = this.previewImageTarget;

    inputField.value = '';

    previewImage.src = '';
    this.hidePreview();

    if (this.autoSubmit() == 'true') {
      inputField.closest('form').submit();
    }
  }

  setupTranslations (customTranslations) {
    if (this.translationsExists(customTranslations)) {
      const translations = JSON.parse(customTranslations);

      const parsedTranslations = {
        'en': {
          'or': translations.sources.local.drop_or,
          'local': {
            'browse': translations.sources.local.title,
            'dd_title_multi': translations.sources.local.drop_file,
            'dd_title_single': translations.sources.local.select_file,
            'drop_title_multiple': translations.sources.local.drop_file,
            'drop_title_single': translations.sources.local.select_file
          },
          'menu': {
            'files': translations.sources.local.title
          },
          'actions': {
            'upload': translations.progress.uploading
          },
          'crop': {
            'title': translations.crop.title,
            'crop_btn': translations.crop.crop_btn,
            'skip_btn': translations.crop.skip_btn,
            'reset_btn': translations.crop.reset_btn,
            'close_btn': translations.crop.close_btn,
            'close_prompt': translations.crop.close_prompt,
            'image_error': translations.crop.image_error,
            'corner_tooltip': translations.crop.corner_tooltip,
            'handle_tooltip': translations.crop.handle_tooltip
          },
          'queue': {
            'title': translations.queue.title,
            'title_uploading_with_counter': translations.queue.title_uploading_with_counter,
            'title_uploading': translations.queue.title_uploading,
            'mini_title': translations.queue.mini_title,
            'mini_title_uploading': translations.queue.title_uploading_with_counter,
            'retry_failed': translations.queue.retry_failed,
            'abort_all': translations.queue.abort_all,
            'done': translations.queue.done,
            'statuses': {
                'uploading': translations.queue.statuses.uploading,
                'error': translations.queue.statuses.error,
                'uploaded': translations.queue.statuses.uploaded,
                'aborted': translations.queue.statuses.aborted
            },
          },
        }
      };

      return parsedTranslations;
    } else {
      return {};
    }
  }

  setupCloudinaryUploadWidget () {
    const apiKey = this.apiKey();
    const autoSubmit = this.autoSubmit();
    const cloudName = this.cloudName();
    const contextMetadata = this.getContextMetadata();
    const controller = this;
    const customTranslations = this.customTranslations();
    const inputField = this.inputFieldTarget;
    const multiple = this.multiple();
    const linkTarget = this.openLinkTarget;
    const previewContainer = this.hasPreviewContainerTarget ? this.previewContainerTarget : null;
    const previewImage = this.hasPreviewImageTarget ? this.previewImageTarget : null;
    const uploadPreset = this.defaultUploadPreset();

    const translations = this.setupTranslations(customTranslations)

    linkTarget.addEventListener('click', (event) => {
      cloudinary.openUploadWidget(
        {
          apiKey: apiKey,
          cloudName: cloudName,
          context: contextMetadata,
          multiple: multiple,
          showPoweredBy: false,
          sources: ["local"],
          text: translations,
          theme: 'white',
          uploadPreset: uploadPreset
        }, function(error, result) {
          if(error && error.message) {
          } else {
            if (result.info && result.info.files) {
              // Handle singular file upload
              if (!multiple) {
                let file = result.info.files[0];

                if (file.uploadInfo.path) {
                  const imageUrl = controller.imageUrl(file.uploadInfo.path);

                  inputField.value = file.uploadInfo.path;

                  // If a preview image is set, update source to match
                  if (previewImage) {
                    previewImage.src = imageUrl;
                  }

                  controller.showPreview();

                  if (autoSubmit == 'true') {
                    inputField.closest('form').submit();
                  }
                }
              } else {
                // Handle multiple file uploads, possibly
                const paths = []
                result.info.files.forEach((file) => {
                  if (file.uploadInfo.path) {
                    paths.push(file.uploadInfo.path)
                  }
                })
                inputField.value = paths.join(",")
                if (autoSubmit == 'true') {
                  inputField.closest('form').submit();
                }
              }
            }
          }
        }
      );
      event.stopPropagation();
      return false;
    });
  }

  showPreviewOnConnect (value) {
    if (value && this.hasPreviewImageTarget) {
      const imageUrl = value.startsWith('http') ? value : this.imageUrl(value)
      this.previewImageTarget.src = imageUrl
      this.showPreview()
    } else {
      this.hidePreview()
    }
  }

  showPreview () {
    if (!this.hasPreviewContainerTarget) return;

    this.previewContainerTarget.classList.remove(this.hiddenClass);
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.classList.remove(this.hiddenClass);
    }
    if (this.hasRemoveTarget) {
      this.removeTarget.classList.remove(this.hiddenClass);
    }
  }
}
